import type { ReactNode } from "react"

import { cn } from "@/lib/utils"
import { toneDotClass, type StatusTone } from "@/lib/status-colors"

/**
 * The shared row vocabulary of the reports screen.
 *
 * Deliberately the same shape as `features/staff/till-report.tsx`: a `space-y-2`
 * section under an `h3 text-sm font-semibold`, a `grid grid-cols-2 sm:grid-cols-3`
 * of figures, then a `ul divide-y rounded-md border` of rows. Two screens that
 * describe the same evening should read the same way, and the manager who signed
 * last night's Z is the person opening this one.
 *
 * `grid-cols-2` is the base and `sm:grid-cols-3` the widening, which is what
 * makes the whole screen mobile-first without a single mobile-only branch: the
 * owner checking how Saturday is going is doing it from their phone.
 *
 * Every figure is `tabular-nums`, and colour never lands on one — it lives in a
 * `size-2` dot beside the label or in the `h-2` bar under the row.
 */

export function ReportSection({
  title,
  hint,
  testId,
  children,
}: {
  title: string
  hint?: string
  testId?: string
  children: ReactNode
}) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </section>
  )
}

/** A `grid-cols-2 sm:grid-cols-3` band of labelled figures. */
export function ReportFigures({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">{children}</div>
}

/** A status always wins the dot; an ordinal mark only fills an empty slot. */
function rowMarkClass(tone?: StatusTone, markClass?: string): string | null {
  if (tone) return toneDotClass(tone, "size-2")
  if (markClass) return cn("size-2 rounded-full", markClass)

  return null
}

export function ReportRow({
  label,
  sublabel,
  meta,
  amount,
  tone,
  markClass,
  testId,
}: {
  label: string
  sublabel?: string
  meta?: string
  /** Already formatted. `null` for a block that counts without moving money. */
  amount?: string | null
  tone?: StatusTone
  /**
   * The fill of the leading `size-2` dot, when the mark is not a STATUS.
   *
   * The payment-method rows use it to tie each row to its slab in the
   * proportion bar above them. It is redundant encoding, not a key: the label it
   * sits beside says which rail this is, so the row reads identically in
   * grayscale. `tone` still wins where a row carries real state — a status has
   * a meaning this does not.
   */
  markClass?: string
  testId?: string
}) {
  const mark = rowMarkClass(tone, markClass)

  return (
    <li className="flex items-center gap-3 px-4 py-2" data-testid={testId}>
      {mark ? <span aria-hidden="true" className={cn("shrink-0", mark)} /> : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{label}</span>
        {sublabel ? (
          <span className="block truncate text-xs text-muted-foreground">{sublabel}</span>
        ) : null}
      </span>
      {meta ? (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{meta}</span>
      ) : null}
      {amount === null || amount === undefined ? null : (
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          {amount}
        </span>
      )}
    </li>
  )
}

/**
 * A row with an `h-2` segment under it, sized against the busiest bucket.
 *
 * The day and hour series used to render as these; #391 moved them to real
 * columns in `charts/`, because a month of service days is not four rows and a
 * list that long stops being scannable long before the data stops being useful.
 *
 * The row survives that move deliberately. It IS a direct-labelled horizontal
 * bar chart — label, exact figure and magnitude on one line — and that is the
 * right form for anything whose categories are long strings. The product mix is
 * the standing example and the reason it was NOT converted: dish names are long
 * Spanish phrases, and a recharts category axis truncates them into uselessness
 * at any width, let alone at 375px. Rebuilding that block as a chart would trade
 * a readable list for an unreadable one.
 */
export function ReportBarRow({
  label,
  meta,
  amount,
  percent,
  testId,
}: {
  label: string
  meta?: string
  amount: string
  percent: number
  testId?: string
}) {
  return (
    <li className="space-y-1.5 px-4 py-2" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
        {meta ? (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{meta}</span>
        ) : null}
        <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          {amount}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div className="h-2 rounded-full bg-primary" style={{ width: `${percent}%` }} />
      </div>
    </li>
  )
}

export function ReportList({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <ul className="divide-y rounded-md border" data-testid={testId}>
      {children}
    </ul>
  )
}
